/**
 * Ajout d'une 2e pièce depuis le panier (snippets/cart-second-piece.liquid).
 *
 * Pastilles de coloris et menu de taille choisissent la variante, la rangée
 * « + » l'ajoute. L'ajout se fait ici plutôt que par un formulaire
 * `add-to-cart-form` : sur la fiche, assets/pack-cart.js intercepte ces
 * formulaires pour y mettre le pack composé sur la page.
 *
 * Même chemin que pack-cart.js : une requête /cart/add.js, puis l'événement de
 * panier avec l'état complet, qui fait redessiner le volet, la pastille et la
 * barre de livraison.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** @typedef {{ id: number, available: boolean, options: string[] }} Variant */

class CartSecondPiece extends HTMLElement {
  /** @type {Variant[]} */
  variants = [];

  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-second-piece-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleOutsideClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleOutsideClick);
  }

  get colour() {
    const pressed = this.querySelector('[data-second-piece-colour][aria-pressed="true"]');
    return pressed instanceof HTMLElement ? pressed.dataset.secondPieceColour || '' : '';
  }

  get toggle() {
    const toggle = this.querySelector('[data-second-piece-size-toggle]');
    return toggle instanceof HTMLButtonElement ? toggle : null;
  }

  get list() {
    const list = this.querySelector('[role="listbox"]');
    return list instanceof HTMLElement ? list : null;
  }

  get size() {
    return this.toggle?.dataset.value || '';
  }

  /**
   * Ouvre ou ferme la liste des tailles, comme les lignes de la fiche
   * (assets/pack-selection.js) : focus sur la taille retenue à l'ouverture,
   * retour au bouton à la fermeture.
   * @param {boolean} open
   * @param {boolean} [focus]
   */
  setOpen(open, focus = false) {
    const { toggle, list } = this;
    if (!toggle || !list) return;

    list.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (open) this.placeList(list);
    if (!focus) return;

    // 🛑 Sans `preventScroll`, le focus faisait défiler le volet jusqu'à la
    // liste : tout le panier sautait à l'ouverture. Constat du principal,
    // 2026-09-28.
    const target = open ? list.querySelector('[aria-selected="true"]') || list.firstElementChild : toggle;
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /**
   * La liste s'ouvre sous son bouton, comme sur la fiche. Seule exception : si
   * elle dépasse du bas de la zone qui défile, elle s'ouvre au-dessus, pour
   * rester lisible sans faire bouger le panier.
   * @param {HTMLElement} list
   */
  placeList(list) {
    list.removeAttribute('data-drop');
    const scroller = this.closest('.cart-drawer__items, scroll-hint');
    const bottom = scroller instanceof HTMLElement ? scroller.getBoundingClientRect().bottom : window.innerHeight;
    const rect = list.getBoundingClientRect();
    const toggleTop = this.toggle?.getBoundingClientRect().top ?? 0;
    if (rect.bottom > bottom && toggleTop - rect.height - 4 > 0) list.setAttribute('data-drop', 'up');
  }

  /** @param {string} size */
  selectSize(size) {
    const { toggle, list } = this;
    if (!toggle || !list || !size) return;

    toggle.dataset.value = size;
    toggle.textContent = size;
    toggle.setAttribute('aria-label', `${list.dataset.sizeLabel || ''}, ${size}`);
    for (const option of list.querySelectorAll('[data-second-piece-size-option]')) {
      option.setAttribute('aria-selected', String(option instanceof HTMLElement && option.dataset.secondPieceSizeOption === size));
    }
    this.setOpen(false, true);
    this.showError('');
  }

  /** Un clic hors de la carte referme la liste ouverte. */
  handleOutsideClick = (/** @type {Event} */ event) => {
    if (this.list?.hidden !== false || event.composedPath().includes(this)) return;
    this.setOpen(false);
  };

  /**
   * Clavier de la liste, sur le modèle d'une liste déroulante : flèches pour
   * ouvrir et parcourir, Entrée ou Espace pour choisir, Échap pour refermer,
   * Tab pour sortir en refermant.
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target === this.toggle && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      this.setOpen(true, true);
      return;
    }

    if (target.dataset.secondPieceSizeOption === undefined) return;
    const options = [...(target.parentElement?.children || [])].filter((node) => node instanceof HTMLElement);
    const position = options.indexOf(target);
    /** @param {number} next */
    const move = (next) => {
      event.preventDefault();
      const option = options[Math.max(0, Math.min(next, options.length - 1))];
      if (option instanceof HTMLElement) option.focus({ preventScroll: true });
    };

    switch (event.key) {
      case 'ArrowDown':
        move(position + 1);
        break;
      case 'ArrowUp':
        move(position - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectSize(target.dataset.secondPieceSizeOption || '');
        break;
      case 'Escape':
        event.preventDefault();
        this.setOpen(false, true);
        break;
      case 'Tab':
        this.setOpen(false);
        break;
    }
  };

  /** @returns {Variant | undefined} */
  get variant() {
    const colourIndex = parseInt(this.dataset.colourIndex || '-1', 10);
    const sizeIndex = parseInt(this.dataset.sizeIndex || '-1', 10);
    return this.variants.find(
      (variant) =>
        (colourIndex < 0 || variant.options[colourIndex] === this.colour) &&
        (sizeIndex < 0 || variant.options[sizeIndex] === this.size)
    );
  }

  /** @param {Event} event */
  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-second-piece-size-toggle]')) {
      this.setOpen(this.list?.hidden !== false, true);
      return;
    }

    const option = target.closest('[data-second-piece-size-option]');
    if (option instanceof HTMLElement) {
      this.selectSize(option.dataset.secondPieceSizeOption || '');
      return;
    }

    // Tout autre clic dans la carte referme la liste ouverte.
    if (this.list?.hidden === false) this.setOpen(false);

    const swatch = target.closest('[data-second-piece-colour]');
    if (swatch instanceof HTMLElement) {
      for (const other of this.querySelectorAll('[data-second-piece-colour]')) {
        other.setAttribute('aria-pressed', String(other === swatch));
      }
      const name = this.querySelector('[data-second-piece-colour-name]');
      if (name) name.textContent = swatch.dataset.secondPieceColour || '';
      this.showError('');
      return;
    }

    const add = target.closest('[data-second-piece-add]');
    if (add instanceof HTMLButtonElement) this.add(add);
  };

  /** @param {HTMLButtonElement} button */
  async add(button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    const variant = this.variant;
    if (!variant?.available) {
      this.showError(this.dataset.error || '');
      return;
    }

    button.setAttribute('aria-busy', 'true');
    this.showError('');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const routes = window.Theme?.routes || {};
    // Le rendu du panier est demandé avec l'ajout, comme dans pack-cart.js :
    // le volet se redessine sans refaire sa propre requête.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    try {
      // `cart_add_url` porte déjà son « .js » (snippets/scripts.liquid).
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
          ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
        }),
      });
      if (!response.ok) throw new Error('add');
      const added = await response.json().catch(() => ({}));

      // La réponse ne porte que la ligne ajoutée : les abonnés attendent le
      // panier entier.
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`).then((result) => result.json());
      deferred.resolve?.({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-second-piece',
          sections: added.sections || undefined,
        },
      });
    } catch (error) {
      deferred.reject?.(error);
      button.removeAttribute('aria-busy');
      this.showError(this.dataset.error || '');
    }
  }

  /** @param {string} message */
  showError(message) {
    const error = this.querySelector('[data-second-piece-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = message === '';
  }
}

if (!customElements.get('cart-second-piece')) {
  customElements.define('cart-second-piece', CartSecondPiece);
}
